export enum Directory {
  /**
   * The Documents directory.
   * On iOS it's the app's documents directory.
   * Use this directory to store user-generated content.
   * On Android it's the Public Documents folder, so it's accessible from other apps.
   * It's not accessible on Android 10 unless the app enables legacy External Storage
   * by adding `android:requestLegacyExternalStorage="true"` in the `application` tag
   * in the `AndroidManifest.xml`.
   * On Android 11 or newer the app can only access the files/folders the app created.
   *
   * @since 1.0.0
   */
  Documents = "DOCUMENTS",

  /**
   * The Data directory.
   * On iOS it will use the Documents directory.
   * On Android it's the directory holding application files.
   * Files will be deleted when the application is uninstalled.
   *
   * @since 1.0.0
   */
  Data = "DATA",

  /**
   * The Library directory.
   * On iOS it will use the Library directory.
   * On Android it's the directory holding application files.
   * Files will be deleted when the application is uninstalled.
   *
   * @since 1.1.0
   */
  Library = "LIBRARY",

  /**
   * The Cache directory.
   * Can be deleted in cases of low memory, so use this directory to write app-specific files.
   * that your app can re-create easily.
   *
   * @since 1.0.0
   */
  Cache = "CACHE",

  /**
   * The external directory.
   * On iOS it will use the Documents directory.
   * On Android it's the directory on the primary shared/external
   * storage device where the application can place persistent files it owns.
   * These files are internal to the applications, and not typically visible
   * to the user as media.
   * Files will be deleted when the application is uninstalled.
   *
   * @since 1.0.0
   */
  External = "EXTERNAL",

  /**
   * The external storage directory.
   * On iOS it will use the Documents directory.
   * On Android it's the primary shared/external storage directory.
   * It's not accessible on Android 10 unless the app enables legacy External Storage
   * by adding `android:requestLegacyExternalStorage="true"` in the `application` tag
   * in the `AndroidManifest.xml`.
   * It's not accessible on Android 11 or newer.
   *
   * @since 1.0.0
   */
  ExternalStorage = "EXTERNAL_STORAGE",
  /**
   * The external cache directory.
   * On iOS it will use the Documents directory.
   * On Android it's the primary shared/external cache.
   *
   * @since 7.1.0
   */
  ExternalCache = "EXTERNAL_CACHE",

  /**
   * The Library directory without cloud backup. Used in iOS.
   * On Android it's the directory holding application files.
   *
   * @since 7.1.0
   */
  LibraryNoCloud = "LIBRARY_NO_CLOUD",

  /**
   * A temporary directory for iOS.
   * On Android it's the directory holding the application cache.
   *
   * @since 7.1.0
   */
  Temporary = "TEMPORARY"
}
/** Http headers shape used by options. */

export interface HttpHeaders {
  [key: string]: string;
}
/** URL query params shape used by options. */

export interface HttpParams {
  [key: string]: string | number | boolean;
}
/** Progress event payload. */

export interface ProgressStatus {
  /** The type of transfer operation (download or upload). */
  type: "download" | "upload";
  /** The url associated with the transfer. */
  url: string;
  /** Bytes transferred so far. */
  bytes: number;
  /** Total number of bytes if known. */
  contentLength: number;
  /** Whether contentLength is known. */
  lengthComputable: boolean;
}
/** Download options (subset + documented fields). */

export interface DownloadFileOptions {
  /** The URL to download the file from. */
  url: string;
  /**
   * Full destination file path (native: file URI).
   * Use @capacitor/filesystem.getUri() to build a full path.
   */
  path: string;
  /** Emit progress events? (native throttled every ~100ms) */
  progress?: boolean;
  /** HTTP method (default: GET) */
  method?: string;
  /** Query string parameters */
  params?: HttpParams;
  /** Request headers (e.g., Authorization) */
  headers?: HttpHeaders;
  /** Read timeout (ms). Not supported on web. */
  readTimeout?: number;
  /** Connect timeout (ms). iOS conflates with read. Not supported on web. */
  connectTimeout?: number;
  /** Disable automatic redirects. Not supported on web. */
  disableRedirects?: boolean;
  /**
   * Whether to URL-encode params automatically (default: true).
   * Not supported on web.
   */
  shouldEncodeUrlParams?: boolean;
}
/** Download result payload. */

export interface DownloadFileResult {
  path: string;
}

export interface PluginListenerHandle {
  remove: () => Promise<void> | void;
}

/**
 * Scan result returned to app code.
 */
export interface CapacitorBarcodeScannerScanResult {
  ScanResult: string;
  format: CapacitorBarcodeScannerTypeHint;
}

export enum Html5QrcodeSupportedFormats {
  QR_CODE = 0,
  AZTEC = 1,
  CODABAR = 2,
  CODE_39 = 3,
  CODE_93 = 4,
  CODE_128 = 5,
  DATA_MATRIX = 6,
  MAXICODE = 7,
  ITF = 8,
  EAN_13 = 9,
  EAN_8 = 10,
  PDF_417 = 11,
  RSS_14 = 12,
  RSS_EXPANDED = 13,
  UPC_A = 14,
  UPC_E = 15,
  UPC_EAN_EXTENSION = 16
}

/**
 * Enum representing a special option to indicate that all barcode types are supported.
 */
export enum CapacitorBarcodeScannerTypeHintALLOption {
  ALL = 17
}

export type CapacitorBarcodeScannerTypeHint =
  | Html5QrcodeSupportedFormats
  | CapacitorBarcodeScannerTypeHintALLOption;

/**
 * Enum representing the orientation of the scanner during barcode scanning.
 */
export enum CapacitorBarcodeScannerScanOrientation {
  PORTRAIT = 1,
  LANDSCAPE = 2,
  ADAPTIVE = 3,
}

/**
 * Enum representing the library to be used for barcode scanning on Android devices.
 */
export enum CapacitorBarcodeScannerAndroidScanningLibrary {
  ZXING = "zxing",
  MLKIT = "mlkit",
}

export enum CapacitorBarcodeScannerCameraDirection {
  BACK = 1,
  FRONT = 2,
}

export interface ConfirmOptions {
  message: string;
  title?: string;
  buttonLabels: string[];
}

export interface AlertOptions {
  message: string;
  title?: string;
  buttonName?: string;
}

export type ConfirmCallback = (buttonIndex: number) => void;
export type AlertCallback = () => void;

export enum BiometryType {
  None = "none",
  TouchId = "touch",
  FaceId = "face",
  Finger = "finger",
  Biometric = "biometric"
}

export enum PluginError {
  BIOMETRIC_UNKNOWN_ERROR = -100,
  BIOMETRIC_UNAVAILABLE = -101,
  BIOMETRIC_AUTHENTICATION_FAILED = -102,
  BIOMETRIC_SDK_NOT_SUPPORTED = -103,
  BIOMETRIC_HARDWARE_NOT_SUPPORTED = -104,
  BIOMETRIC_PERMISSION_NOT_GRANTED = -105,
  BIOMETRIC_NOT_ENROLLED = -106,
  // When fingerprints are not enrolled, Samsung Galaxy S series returned an error code 11.
  BIOMETRIC_NOT_ENROLLED_II = 11,
  BIOMETRIC_INTERNAL_PLUGIN_ERROR = -107,
  BIOMETRIC_DISMISSED = -108,
  BIOMETRIC_PIN_OR_PATTERN_DISMISSED = -109,
  BIOMETRIC_SCREEN_GUARD_UNSECURED = -110,
  BIOMETRIC_LOCKED_OUT = -111,
  BIOMETRIC_LOCKED_OUT_PERMANENT = -112,
  BIOMETRIC_NO_SECRET_FOUND = -113,
}

export interface RegisterBiometricOptions {
  title: string;
  subtitle?: string;
  description: string;
  fallbackButtonTitle?: string;
  cancelButtonTitle?: string;
  confirmationRequired?: boolean;
  secret: string;
  disableBackup?: boolean;
  invalidateOnEnrollment?: boolean;
}

export enum ToolbarPosition {
  TOP = 0,
  BOTTOM = 1
}
export enum IOSViewStyle {
  PAGE_SHEET = 0,
  FORM_SHEET = 1,
  FULL_SCREEN = 2
}
export enum AndroidViewStyle {
  BOTTOM_SHEET = 0,
  FULL_SCREEN = 1
}
export enum IOSAnimation {
  FLIP_HORIZONTAL = 0,
  CROSS_DISSOLVE = 1,
  COVER_VERTICAL = 2
}
export enum AndroidAnimation {
  FADE_IN = 0,
  FADE_OUT = 1,
  SLIDE_IN_LEFT = 2,
  SLIDE_OUT_RIGHT = 3
}
export interface WebViewOptions {
  /** Displays the URL on the Web View. */
  showURL: boolean;
  /** Displays the toolbar on the Web View. */
  showToolbar: boolean;
  /** Clears the Web View's cookie cache before a new window is opened. */
  clearCache: boolean;
  /** Clears the session cookie cache before a new window is opened. */
  clearSessionCache: boolean;
  /** Prevents HTML5 audio or video from auto-playing. */
  mediaPlaybackRequiresUserAction: boolean;
  /** Sets the text to display on the Close button on the Web View. */
  closeButtonText: string;
  /** Sets the position to display the Toolbar on the Web View. */
  toolbarPosition: ToolbarPosition;
  /** Displays the navigation buttons. */
  showNavigationButtons: boolean;
  /** Swaps the positions of the navigation buttons and the close button. */
  leftToRight: boolean;
  /** Sets a custom user agent to open the Web View with. If empty or not set, the parameter will be ignored. */
  customWebViewUserAgent?: string | null;
  /** Android-specific Web View options. */
  android: AndroidWebViewOptions;
  /** iOS-specific Web View options. */
  iOS: IOSWebViewOptions;
}
export interface IOSWebViewOptions {
  /** Turns on the Web View bounce property. */
  allowOverScroll: boolean;
  /** Prevents viewport scaling through a meta tag. */
  enableViewportScale: boolean;
  allowInLineMediaPlayback: boolean;
  /** Waits until all new view content is received before being rendered. */
  surpressIncrementalRendering: boolean;
  /** Sets the presentation style of the Web View. */
  viewStyle: IOSViewStyle;
  /** Sets the transition style of the Web View. */
  animationEffect: IOSAnimation;
  /** Enables back and forward swipe gestures in the Web View. */
  allowsBackForwardNavigationGestures: boolean;
}
export interface AndroidWebViewOptions {
  /** Shows the Android browser's zoom controls. */
  allowZoom: boolean;
  /** Uses the hardware back button to navigate backwards through the Web View's history. If there is no previous page, the Web View will close. */
  hardwareBack: boolean;
  /** Makes the Web View pause/resume with the app to stop background audio. */
  pauseMedia: boolean;
  /** Whether to run the InAppBrowser in an isolated process. Android only. Defaults to true. */
  isIsolated?: boolean;
}
export enum DismissStyle {
  CLOSE = 0,
  CANCEL = 1,
  DONE = 2
}
export interface SystemBrowserOptions {
  /** Android-specific System Browser options. */
  android: AndroidSystemBrowserOptions;
  /** iOS-specific System Browser options. */
  iOS: IOSSystemBrowserOptions;
}
export interface IOSSystemBrowserOptions {
  /** Sets a text to use as the close button's caption. */
  closeButtonText: DismissStyle;
  /** Sets the presentation style of SafariViewController. */
  viewStyle: IOSViewStyle;
  /** Sets the transition style of SafariViewController. */
  animationEffect: IOSAnimation;
  /** Enables bars to collapse on scrolling down. */
  enableBarsCollapsing: boolean;
  /** Enables readers mode. */
  enableReadersMode: boolean;
}
export interface AndroidBottomSheet {
  height: number;
  /** Sets whether the bottom sheet is fixed. */
  isFixed: boolean;
}
export interface AndroidSystemBrowserOptions {
  /** Enables the title display. */
  showTitle: boolean;
  /** Hides the toolbar when scrolling. */
  hideToolbarOnScroll: boolean;
  /** Sets the presentation style of CustomTabs. */
  viewStyle: AndroidViewStyle;
  /** Sets the options for the bottom sheet when this is selected as the viewStyle. If viewStyle is FULL_SCREEN, this will be ignored. */
  bottomSheetOptions?: AndroidBottomSheet;
  /** Sets the start animation for when the browser appears. */
  startAnimation: AndroidAnimation;
  /** Sets the exit animation for when the browser disappears. */
  exitAnimation: AndroidAnimation;
}
/**
 * Defines the options for opening a URL in the external browser and used by the others.
 */
export interface OpenInDefaultParameterModel {
  /** The URL to be opened. It must contain either 'http' or 'https' as the protocol prefix. */
  url: string;
}
/**
 * Defines the options for opening a URL in the system browser.
 */
export interface OpenInSystemBrowserParameterModel extends OpenInDefaultParameterModel {
  /** A structure containing some configurations to apply to the System Browser. */
  options: SystemBrowserOptions;
}
/**
 * Defines the options for opening a URL in the web view.
 */
export interface OpenInWebViewParameterModel extends OpenInDefaultParameterModel {
  /** A structure containing some configurations to apply to the Web View. */
  options: WebViewOptions;
  /** A map of custom headers to be sent with the request. */
  customHeaders?: {
    [key: string]: string;
  };
}
/**
 * Defines the data for the 'browserPageNavigationCompleted' event.
 */
export interface BrowserPageNavigationCompletedEventData {
  /** The URL of the page that was loaded. */
  url: string | undefined;
}
